import React, { useState, useEffect, useCallback } from "react";
import DayPicker from "react-day-picker";
import "react-day-picker/lib/style.css";
import moment from "moment";
import "moment/locale/nb";

import classes from "./style.module.scss";
import { useAppHeader } from "~/app/modules/appHeader";
import usePageTitle from "~/app/hooks/usePageTitle";
import AccessControl from "~/accessControl";
import Button from "~/app/common/Button";
import Icon from "~/app/common/Icon";
import { PROJECTS_ROUTES } from "~/projects/Router";
import { PERMISSION_GROUPS } from "~/accessControl/permissionGroups";
import { TIME_MANAGEMENT_ROUTES } from "../Router";
import { TIME_MODE_TYPE } from "~/timeManagement/timeModeType";
import { route } from "~/app/helpers/route";
import { TASK_ROUTES } from "~/tasks/Router";
import { MaterialIcons as Icons } from '~/app/common/Icon/icons';
import { useHistory } from "react-router-dom";
import useTranslation from "~/app/hooks/useTranslation";
import { useStoreActions, useStoreState } from "~/store";
import { DAY_STATUS } from "~/app/data/statuses/timeManagementStatus";

moment.locale("nb");

const Detail: React.FC = () => {
  const [entries, setEntries] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [selectedDay, setSelectedDay] = useState<Date | undefined>();
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [showDetails, setShowDetails] = useState(false);
  const [selectedEntryData, setSelectedEntryData] = useState<any[]>([]);
  const hasTimeforingModule = useStoreState(state => state.tenant.hasTimeforingModule);

  const fetching = useStoreState(state => state.timeManagement.fetching);
  const standardWorkingHoursPerDay = useStoreState(
    state => state.machines.standardWorkingHoursPerDay
  );
  const TimeListAction = useStoreActions(actions => actions.timeManagement.getAllTimeData);

  const { t, i18n } = useTranslation('timeManagement');
  const { t: tb } = useTranslation('bottomNav');
  const history = useHistory();

  usePageTitle(t('pageTitle'));

  const getWeekDates = (): moment.Moment[] => {
    const selected = selectedDay || new Date();
    const dates: moment.Moment[] = [];
    for (let i = -3; i <= 3; i++) {
      const date = moment(selected).add(i, 'days');
      dates.push(date);
    }
    return dates;
  };

  const fetchEntries = useCallback(async() => {
    setError(null);
    try {
      const res = await TimeListAction({});
      const dataArray = Array.isArray(res) ? res : [];
      setEntries(dataArray);
    } catch (err: any) {
      setError(err.message || t('failedToFetchData'));
      setEntries([]);
      console.error("Fetch error:", err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [TimeListAction]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const entryDates: Date[] = entries.flatMap((project) =>
    project.entries?.map((e: any) => new Date(e.date)) || []
  );

  const filteredProjects = entries.map((project) => {
    const filteredEntries = project.entries?.filter((entry: any) => {
      if (!entry.date) return false;
      const entryDate = moment(entry.date, "YYYY-MM-DD");
      const sameMonth =
        entryDate.month() === currentMonth.getMonth() &&
        entryDate.year() === currentMonth.getFullYear();

      return sameMonth;
    }) || [];

    return {
      ...project,
      entries: filteredEntries
    };
  }).filter(project => project.entries.length > 0);

  const { setData, clearData } = useAppHeader();

  useEffect(() => {
    const monthName = currentMonth.toLocaleString("no-NO", {
      month: "long",
      year: "numeric",
    });

    setData({
      title: monthName,
      subTitle: '',
      backLink: () => history.replace(route(TIME_MANAGEMENT_ROUTES.list)),
      isTimeManagementPage: false,
      isAddTimeForm: true,
    });

    return () => clearData();
  }, [currentMonth, setData, clearData, history]);

  const handleEditEntry = (entry: any) => {
    if (entry.is_absence && entry.absence_registration_id) {
      const hoursDec =
        entry.hours_decimal != null && entry.hours_decimal !== ''
          ? Number(entry.hours_decimal)
          : (() => {
              const timeStr = entry.time || '00:00';
              const parts = timeStr.split(':').map((x: string) => parseInt(x, 10) || 0);
              const hh = parts[0] ?? 0;
              const mm = parts[1] ?? 0;
              return (hh * 60 + mm) / 60;
            })();
      history.push({
        pathname: route(TIME_MANAGEMENT_ROUTES.addTimeForm),
        state: {
          editAbsence: {
            absence_registration_id: Number(entry.absence_registration_id),
            absence_id: Number(entry.activity_id),
            hours_decimal: Number.isFinite(hoursDec) ? hoursDec : standardWorkingHoursPerDay,
            notes: entry.notes || '',
            other_absence_reason: entry.other_absence_reason || '',
            date: entry.date,
          },
        },
      });
      return;
    }
    if (entry.id) {
      history.push(`/timeManagement/${entry.id}`);
    }
  };

  const getModeBadge = (modeType: number, hasMachine: boolean, hasActivity: boolean) => {

    if (modeType === 0 && hasMachine && hasActivity) {
      return { label: 'Maskin', className: classes.badgeMachine };
    }
    if (
      modeType === TIME_MODE_TYPE.TASK ||
      (modeType === TIME_MODE_TYPE.MACHINE && !hasMachine && hasActivity)
    ) {
      return { label: 'Oppgave', className: classes.badgeTask };
    }
    if (modeType === TIME_MODE_TYPE.UNSPECIFIED) {
      return { label: 'Uspesifisert', className: classes.badgeUnspecified };
    }

    return null;
  };

  const getDayStatus = useCallback((day: Date) => {
    const formattedDay = moment(day).format('YYYY-MM-DD');
    for (const project of entries) {
      if (!project.entries) continue;
      const entry = project.entries.find((e: any) => e.date === formattedDay);
      if (entry && entry.status !== undefined) {
        return entry.status;
      }
    }
    return null;
  }, [entries]);

  return (
    <div className={classes["detail-wrapper"]}>
      {/* Calendar Section */}
      <div className={classes["calendar-box"]}>
        <div className={classes["simple-header"]}>
          <button
            className={classes["arrow-btn"]}
            onClick={() => setCurrentMonth(moment(currentMonth).subtract(1, 'month').toDate())}
          >
            ‹
          </button>
          <h2 className={classes["month-text"]}>
            {currentMonth.toLocaleString("no-NO", { month: "long", year: "numeric" })}
          </h2>
          <button
            className={classes["arrow-btn"]}
            onClick={() => setCurrentMonth(moment(currentMonth).add(1, 'month').toDate())}
          >
            ›
          </button>
        </div>

        <DayPicker
          month={currentMonth}
          onMonthChange={setCurrentMonth}
          selectedDays={selectedDay}
          modifiers={{
            approved: (day: Date) => getDayStatus(day) === DAY_STATUS.APPROVED,
            rejected: (day: Date) => getDayStatus(day) === DAY_STATUS.REJECTED,
            submitted: (day: Date) => getDayStatus(day) === DAY_STATUS.SUBMITTED,
            locked: (day: Date) => getDayStatus(day) === DAY_STATUS.LOCKED,
            hasEntry: (day: Date) => {
              const isInCurrentMonth = day.getMonth() === currentMonth.getMonth() &&
                day.getFullYear() === currentMonth.getFullYear();

              if (!isInCurrentMonth) return false;

              return entryDates.some((d: Date) =>
                d.getFullYear() === day.getFullYear() &&
                d.getMonth() === day.getMonth() &&
                d.getDate() === day.getDate()
              );
            },
          }}
          onDayClick={(day, modifiers) => {
            setSelectedDay(day);

            if (modifiers.hasEntry) {
              const filteredProjects = entries.filter((project) => {
                return project.entries?.some((e: any) =>
                  moment(e.date, "YYYY-MM-DD").isSame(day, "day")
                );
              }).map((project) => {
                return {
                  ...project,
                  entries: project.entries.filter((e: any) =>
                    moment(e.date, "YYYY-MM-DD").isSame(day, "day")
                  )
                };
              });

              setSelectedEntryData(filteredProjects);
              setShowDetails(true);
              return;
            }
            const formattedDate = moment(day).format('YYYY-MM-DD');
            history.push({
              pathname: route(TIME_MANAGEMENT_ROUTES.addTimeForm),
              state: { selectedDate: formattedDate }
            });
          }}
        />
      </div>

      {/* List Header */}
      <div className={classes["list-summary"]}>
        <span className={classes["summary-label"]}>{t('myTimeList')}</span>
        <span className={classes["summary-total"]}>
          {(() => {
            const totalMinutes = filteredProjects.reduce((sum, project) => {
              return sum + (project.entries?.reduce((entrySum: number, e: any) => {
                if (e.total_time != null && Number(e.total_time) > 0) {
                  return entrySum + Number(e.total_time);
                }
                const timeStr = e.time || '00:00';
                const [hours, minutes] = timeStr.split(':').map((t: string) => parseInt(t) || 0);
                return entrySum + hours * 60 + minutes;
              }, 0) || 0);
            }, 0);

            const totalHours = Math.floor(totalMinutes / 60);
            return totalMinutes > 0
              ? `${totalHours} ${t('hours')}`
              : `0 ${t('hours')}`;
          })()}
        </span>
      </div>

      {/* List Container */}
      <div className={classes["list-container"]}>
        {fetching && <p>{t('loading')}</p>}
        {!fetching && error && <p>{t('error')}: {error}</p>}
        {!fetching && filteredProjects.length === 0 && (
          <div className={classes.emptyState}>
            <div className={classes.emptyIcon}>📅</div>
            <h3 className={classes.emptyTitle}>{t('noEntriesThisMonth')}</h3>
            <p className={classes.emptyText}>{t('clickDayToAddTime')}</p>
            <button
              className={classes.emptyButton}
              onClick={() => {
                const todayDate = moment().format('YYYY-MM-DD');
                history.push({
                  pathname: route(TIME_MANAGEMENT_ROUTES.addTimeForm),
                  state: { selectedDate: todayDate }
                });
              }}
            >
              {t('addTimeToday')}
            </button>
          </div>
        )}

        {!fetching && (() => {
          const dateGroups: { [key: string]: any[] } = {};

          filteredProjects.forEach((project) => {
            project.entries.forEach((entry: any) => {
              const dateKey = entry.date;
              if (!dateGroups[dateKey]) {
                dateGroups[dateKey] = [];
              }
              dateGroups[dateKey].push({
                ...entry,
                project_number: project.project_number,
                project_id: project.project_id,
                project_name: project.project_name
              });
            });
          });

          const sortedDates = Object.keys(dateGroups).sort((a, b) =>
            moment(b, "YYYY-MM-DD").valueOf() - moment(a, "YYYY-MM-DD").valueOf()
          );

          return sortedDates.map((date) => {
            const entriesForDate = dateGroups[date];
            const entryMoment = moment(date, "YYYY-MM-DD").locale("nb");

            const totalMinutes = entriesForDate.reduce((sum, e) => {
              if (e.total_time != null && Number(e.total_time) > 0) {
                return sum + Number(e.total_time);
              }
              const timeStr = e.time || '00:00';
              const [hours, minutes] = timeStr.split(':').map((t: string) => parseInt(t) || 0);
              return sum + hours * 60 + minutes;
            }, 0);

            const totalHours = Math.floor(totalMinutes / 60);
            const remainingMinutes = totalMinutes % 60;
            const totalTime = `${String(totalHours).padStart(2, '0')}:${String(remainingMinutes).padStart(2, '0')}t`;

            return (
              <div
                key={date}
                className={classes["date-item"]}
                onClick={() => {
                  const dayDate = moment(date, "YYYY-MM-DD").toDate();
                  setSelectedDay(dayDate);

                  const filteredProjects = entries.filter((project) => {
                    return project.entries?.some((e: any) =>
                      moment(e.date, "YYYY-MM-DD").isSame(dayDate, "day")
                    );
                  }).map((project) => {
                    return {
                      ...project,
                      entries: project.entries.filter((e: any) =>
                        moment(e.date, "YYYY-MM-DD").isSame(dayDate, "day")
                      )
                    };
                  });

                  setSelectedEntryData(filteredProjects);
                  setShowDetails(true);
                }}
              >
                <span className={classes["date-label"]}>{entryMoment.format("dddd D")}</span>
                <span className={classes["date-time"]}>{totalTime}</span>
              </div>
            );
          });
        })()}
      </div>

      {showDetails && (
        <div className={classes.bottomSheet}>
          <div className={classes.sheetHeader}>
            <button onClick={() => setShowDetails(false)} className={classes.backBtn}>←</button>
            <span className={classes.sheetTitle}>
              {moment(selectedDay).format("D. MMMM YYYY")}
            </span>
            <button
              onClick={() => {
                const formattedDate = moment(selectedDay).format('YYYY-MM-DD');
                history.push({
                  pathname: route(TIME_MANAGEMENT_ROUTES.addTimeForm),
                  state: { selectedDate: formattedDate }
                });
              }}
              className={classes.addBtn}
            >
              +
            </button>
          </div>

          <div className={classes.weekSelector}>
            {getWeekDates().map((date, idx) => {
              const isSelected = selectedDay ? date.isSame(selectedDay, 'day') : false;
              const hasEntries = entries.some((project) =>
                project.entries?.some((e: any) => moment(e.date, "YYYY-MM-DD").isSame(date, "day"))
              );
              return (
                <button
                  key={idx}
                  className={`${classes.weekDay} ${isSelected ? classes.weekDaySelected : ''}`}
                  onClick={() => {
                    const newDay = date.toDate();
                    setSelectedDay(newDay);

                    const filteredProjects = entries.filter((project) => {
                      return project.entries?.some((e: any) =>
                        moment(e.date, "YYYY-MM-DD").isSame(newDay, "day")
                      );
                    }).map((project) => {
                      return {
                        ...project,
                        entries: project.entries.filter((e: any) =>
                          moment(e.date, "YYYY-MM-DD").isSame(newDay, "day")
                        )
                      };
                    });

                    setSelectedEntryData(filteredProjects);

                    if (filteredProjects.length === 0) {
                      setSelectedEntryData([]);
                    }
                  }}
                  disabled={!hasEntries}
                  style={{ opacity: hasEntries ? 1 : 0.4 }}
                >
                  <div className={classes.weekDayName}>{date.format('ddd').substring(0, 3)}</div>
                  <div className={classes.weekDayNumber}>{date.format('D')}</div>
                </button>
              );
            })}
          </div>

          <div className={classes.sheetTasksHeader}>
            <span>{t('tasks')}</span>
            <span>
              {(() => {
                const totalMinutes = selectedEntryData.reduce((sum, project) => {
                  return sum + (project.entries?.reduce((entrySum: number, e: any) => {
                    if (e.total_time != null && Number(e.total_time) > 0) {
                      return entrySum + Number(e.total_time);
                    }
                    const timeStr = e.time || '00:00';
                    const [hours, minutes] = timeStr.split(':').map((t: string) => parseInt(t) || 0);
                    return entrySum + hours * 60 + minutes;
                  }, 0) || 0);
                }, 0);

                const totalHours = Math.floor(totalMinutes / 60);
                const remainingMinutes = totalMinutes % 60;
                return `${String(totalHours).padStart(2, '0')}:${String(remainingMinutes).padStart(2, '0')} ${t('hours')}`;
              })()}
            </span>
          </div>

          <div className={classes.sheetTasks}>
            {selectedEntryData.map((project) => {
              const totalMinutes = project.entries?.reduce((sum: number, e: any) => {
                if (e.total_time != null && Number(e.total_time) > 0) {
                  return sum + Number(e.total_time);
                }
                const timeStr = e.time || '00:00';
                const [hours, minutes] = timeStr.split(':').map((t: string) => parseInt(t) || 0);
                return sum + hours * 60 + minutes;
              }, 0) || 0;

              const totalHours = Math.floor(totalMinutes / 60);
              const remainingMinutes = totalMinutes % 60;
              const totalTime = `${String(totalHours).padStart(2, '0')}:${String(remainingMinutes).padStart(2, '0')}t`;

              return (
                <div
                  key={project.is_absence_group ? 'absence-group' : String(project.project_id)}
                  className={classes.projectGroup}
                >
                  <div className={classes.projectHeader}>
                    <span className={classes.projectNumber}>
                      {project.is_absence_group ? t('absenceGroupTitle') : project.project_number}
                    </span>
                    <span className={classes.projectTotal}>{totalTime}</span>
                  </div>

                  {project.entries?.map((entry: any, idx: number) => {
                    const badge = getModeBadge(
                      entry.mode_type,
                      !!entry.machine_name,
                      !!entry.activity_name
                    );

                    return (
                      <div
                        key={entry.id || idx}
                        className={classes.sheetTaskItem}
                        onClick={() => handleEditEntry(entry)}
                        style={{ cursor: entry.is_absence || entry.id ? 'pointer' : 'default' }}
                      >
                        <div className={classes.taskLeft}>
                          <div className={classes.checkmark}>✓</div>
                          <div className={classes.taskDetails}>
                            <div className={classes.taskTopRow}>
                              {badge && (
                                <span className={`${classes.modeBadge} ${badge.className}`}>
                                  {badge.label}
                                </span>
                              )}
                            </div>
                            
                            {entry.is_absence ? (
                              <>
                                {entry.activity_name ? (
                                  <div className={classes.taskMachine}>
                                    {i18n.t(entry.activity_name)}
                                  </div>
                                ) : null}
                                {entry.notes ? (
                                  <div className={classes.activityName}>{entry.notes}</div>
                                ) : null}
                              </>
                            ) : entry.mode_type === 2 ? (
                              <div className={classes.taskMachine}>
                                {t('selectUnspecified')}
                              </div>
                            ) : (
                              <>
                                {entry.machine_name && (
                                  <div className={classes.taskMachine}>
                                    {entry.machine_name}
                                  </div>
                                )}

                                {entry.activity_name && (
                                  <div className={classes.activityName}>
                                    {entry.activity_name}
                                  </div>
                                )}

                                {!entry.machine_name && !entry.activity_name && (
                                  <div className={classes.taskMachine}>
                                    {entry.notes || t('task')}
                                  </div>
                                )}
                              </>
                            )}
                          </div>
                        </div>
                        <strong>{entry.time || "–"}</strong>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <AccessControl requiredPermissions={PERMISSION_GROUPS.oppgaver}>
        <div className={classes['bottomNav']}>
          <Button className={classes['bottomButton']} to={route(PROJECTS_ROUTES.list)}>
            <Icon name={'home' as Icons} className={classes['bottomIcon']} />
            {tb('home')}
          </Button>

          {hasTimeforingModule && (
            <AccessControl requiredPermissions={PERMISSION_GROUPS.timeManagement}>
              <Button
                className={classes['bottomButton']}
                to={route(TIME_MANAGEMENT_ROUTES.list)}
              >
                <Icon name={'schedule' as Icons} className={classes['bottomIcon']} />
                {tb('timeManagement')}
              </Button>
            </AccessControl>
          )}

          <Button className={classes['bottomButton']} to={route(TASK_ROUTES.list)}>
            <Icon name={'assignment' as Icons} className={classes['bottomIcon']} />
            {tb('task')}
          </Button>
        </div>
      </AccessControl>
    </div>
  );
};

export default Detail;